import { useEffect, useState } from "react";
import { addMonths, addWeeks, addDays, format, startOfWeek, endOfWeek } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState } from "@/components/kit";
import CalendarView from "@/components/CalendarView";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Plus, Check, X, Trash2, Settings2, ChevronLeft, ChevronRight, CalendarClock, UserCheck, Pencil, Video, Link2, Copy } from "lucide-react";

const WD = [{ n: 1, l: "Seg" }, { n: 2, l: "Ter" }, { n: 3, l: "Qua" }, { n: 4, l: "Qui" }, { n: 5, l: "Sex" }, { n: 6, l: "Sáb" }, { n: 7, l: "Dom" }];
const PALETTE = ["#6366F1", "#10B981", "#F59E0B", "#EC4899", "#0EA5E9", "#8B5CF6"];

export default function Agenda() {
  const [appts, setAppts] = useState([]);
  const [types, setTypes] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [tForm, setTForm] = useState({ name: "", duration: 60, value: 0, color: PALETTE[0] });
  const [editingType, setEditingType] = useState(null);
  const [availOpen, setAvailOpen] = useState(false);
  const [avail, setAvail] = useState(null);
  const [newBlock, setNewBlock] = useState("");
  const [newHoliday, setNewHoliday] = useState("");
  const [vac, setVac] = useState({ start: "", end: "" });
  const [linkAppt, setLinkAppt] = useState(null);
  const [linkVal, setLinkVal] = useState("");

  const openLink = (a) => { setLinkAppt(a); setLinkVal(a.meeting_url || ""); };
  const saveLink = async () => {
    await api.patch(`/appointments/${linkAppt.id}/meeting-link`, { meeting_url: linkVal });
    toast.success("Link da consulta atualizado"); setLinkAppt(null); load();
  };
  const copyLink = (url) => { navigator.clipboard?.writeText(url); toast.success("Link copiado"); };

  const load = () => {
    api.get("/appointments").then(({ data }) => setAppts(data));
    api.get("/appointment-types").then(({ data }) => setTypes(data));
  };
  useEffect(() => { load(); api.get("/therapist/availability").then(({ data }) => setAvail(data)); }, []);

  const setStatus = async (id, status) => { await api.patch(`/appointments/${id}/status`, { status }); toast.success("Consulta atualizada"); load(); };
  const openNewType = () => { setEditingType(null); setTForm({ name: "", duration: 60, value: 0, color: PALETTE[0] }); setOpen(true); };
  const openEditType = (t) => { setEditingType(t); setTForm({ name: t.name, duration: t.duration, value: t.value, color: t.color || PALETTE[0] }); setOpen(true); };
  const saveType = async () => {
    const payload = { ...tForm, duration: parseInt(tForm.duration), value: parseFloat(tForm.value) };
    if (editingType) await api.put(`/appointment-types/${editingType.id}`, payload);
    else await api.post("/appointment-types", payload);
    toast.success("Tipo de consulta salvo"); setOpen(false); setEditingType(null);
    setTForm({ name: "", duration: 60, value: 0, color: PALETTE[0] }); load();
  };
  const delType = async (id) => { await api.delete(`/appointment-types/${id}`); load(); };

  const saveAvail = async () => { const { data } = await api.put("/therapist/availability", avail); setAvail(data); toast.success("Disponibilidade salva"); setAvailOpen(false); };
  const toggleDay = (n) => setAvail({ ...avail, working_days: avail.working_days.includes(n) ? avail.working_days.filter((x) => x !== n) : [...avail.working_days, n] });

  const step = (dir) => {
    if (view === "month") setCursor(addMonths(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addDays(cursor, dir));
  };
  const rangeLabel = () => {
    if (view === "month") return format(cursor, "MMMM yyyy", { locale: ptBR });
    if (view === "week") return `${format(startOfWeek(cursor, { weekStartsOn: 0 }), "d MMM", { locale: ptBR })} – ${format(endOfWeek(cursor, { weekStartsOn: 0 }), "d MMM", { locale: ptBR })}`;
    return format(cursor, "d 'de' MMMM yyyy", { locale: ptBR });
  };

  const upcoming = appts.filter((a) => ["requested", "confirmed", "rescheduled"].includes(a.status));

  const typeColors = {};
  types.forEach((t, i) => { typeColors[t.name] = t.color || PALETTE[i % PALETTE.length]; });

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayAppts = appts.filter((a) => a.date === todayStr && ["requested", "confirmed", "rescheduled"].includes(a.status));
  const confirmedToday = todayAppts.filter((a) => a.attendance_confirmed).length;

  return (
    <div>
      <PageHeader title="Agenda" subtitle="Visualize consultas e configure sua disponibilidade"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAvailOpen(true)} className="rounded-full" data-testid="availability-btn"><Settings2 className="w-4 h-4 mr-1" />Disponibilidade</Button>
            <Button onClick={openNewType} className="rounded-full" data-testid="new-type-btn"><Plus className="w-4 h-4 mr-1" />Tipo de consulta</Button>
          </div>
        } />

      <div className="mb-4">
        <span data-testid="presence-counter"
          className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-4 py-1.5 text-sm font-medium">
          <UserCheck className="w-4 h-4" />
          {confirmedToday} de {todayAppts.length} cliente(s) confirmaram presença hoje
        </span>
      </div>

      {types.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4" data-testid="color-legend">
          {types.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="w-3 h-3 rounded-full" style={{ background: typeColors[t.name] }} />{t.name}
            </span>
          ))}
        </div>
      )}

      {/* calendar controls */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => step(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Hoje</Button>
            <Button variant="outline" size="icon" onClick={() => step(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
            <span className="font-heading font-medium capitalize ml-2">{rangeLabel()}</span>
          </div>
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="day" data-testid="view-day">Dia</TabsTrigger>
              <TabsTrigger value="week" data-testid="view-week">Semana</TabsTrigger>
              <TabsTrigger value="month" data-testid="view-month">Mês</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <CalendarView view={view} cursor={cursor} appointments={appts} typeColors={typeColors} onSelectDay={(d) => { setCursor(d); setView("day"); }} />
      </Card>

      {/* appointment types */}
      <h3 className="font-heading font-medium mb-3">Tipos de consulta</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {types.map((t) => (
          <Card key={t.id} className="p-4 flex items-center justify-between" data-testid={`type-${t.id}`}>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: typeColors[t.name] }} />
              <div><div className="font-medium">{t.name}</div><div className="text-sm text-muted-foreground">{t.duration} min · R$ {t.value.toFixed(2)}</div></div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => openEditType(t)} data-testid={`edit-type-${t.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => delType(t.id)}><Trash2 className="w-4 h-4" /></Button>
          </Card>
        ))}
        {types.length === 0 && <p className="text-sm text-muted-foreground">Nenhum tipo de consulta configurado.</p>}
      </div>

      {/* pending requests */}
      <h3 className="font-heading font-medium mb-3">Solicitações e próximas consultas</h3>
      {upcoming.length === 0 ? <EmptyState icon={CalendarDays} title="Nenhuma consulta agendada" /> : (
        <div className="space-y-3">
          {upcoming.map((a) => (
            <Card key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`appt-${a.id}`}>
              <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex flex-col items-center justify-center shrink-0">
                <span className="text-xs">{format(new Date(a.date + "T00:00"), "MMM", { locale: ptBR })}</span>
                <span className="font-heading font-semibold text-sm">{a.date.split("-")[2]}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{a.client_name}</span><StatusBadge status={a.status} /></div>
                <p className="text-sm text-muted-foreground">{a.type} · {a.time} · R$ {a.value?.toFixed(2)} · {a.payment_status === "paid" ? "Pago" : "—"}</p>
                {a.modality === "online" && (
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-xs text-primary flex items-center gap-1 min-w-0 max-w-[220px] truncate"><Video className="w-3.5 h-3.5 shrink-0" />{a.meeting_url || "Sem link"}</span>
                    {a.meeting_url && <button onClick={() => copyLink(a.meeting_url)} className="text-muted-foreground hover:text-foreground" data-testid={`copy-link-${a.id}`}><Copy className="w-3.5 h-3.5" /></button>}
                    <button onClick={() => openLink(a)} className="text-xs text-primary font-medium flex items-center gap-1" data-testid={`edit-link-${a.id}`}><Link2 className="w-3.5 h-3.5" />Alterar link</button>
                  </div>
                )}
              </div>
              {a.status === "requested" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setStatus(a.id, "confirmed")} data-testid={`confirm-${a.id}`}><Check className="w-4 h-4 mr-1" />Confirmar</Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => setStatus(a.id, "cancelled")}><X className="w-4 h-4" /></Button>
                </div>
              )}
              {a.status === "confirmed" && <Button size="sm" variant="outline" onClick={() => setStatus(a.id, "completed")}>Concluir</Button>}
            </Card>
          ))}
        </div>
      )}

      {/* type dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingType ? "Editar tipo de consulta" : "Novo tipo de consulta"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="type-name-input" className="mt-1" placeholder="Primeira Consulta" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duração (min)</Label><Input type="number" value={tForm.duration} onChange={(e) => setTForm({ ...tForm, duration: e.target.value })} className="mt-1" /></div>
              <div><Label>Valor (R$)</Label><Input type="number" value={tForm.value} onChange={(e) => setTForm({ ...tForm, value: e.target.value })} data-testid="type-value-input" className="mt-1" /></div>
            </div>
            <div>
              <Label>Cor na agenda</Label>
              <div className="flex gap-2 mt-2">
                {PALETTE.map((c) => (
                  <button key={c} type="button" onClick={() => setTForm({ ...tForm, color: c })} data-testid={`type-color-${c}`}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${tForm.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={saveType} data-testid="save-type-btn">Criar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* availability dialog */}
      <Dialog open={availOpen} onOpenChange={setAvailOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Disponibilidade da agenda</DialogTitle></DialogHeader>
          {avail && (
            <div className="space-y-4">
              <div>
                <Label>Dias de atendimento</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {WD.map((d) => (
                    <button key={d.n} type="button" onClick={() => toggleDay(d.n)} data-testid={`wd-${d.n}`}
                      className={`px-3 py-1.5 rounded-full text-sm border ${avail.working_days.includes(d.n) ? "bg-primary text-white border-primary" : "border-border"}`}>{d.l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Início</Label><Input type="time" value={avail.start_time} onChange={(e) => setAvail({ ...avail, start_time: e.target.value })} className="mt-1" data-testid="avail-start" /></div>
                <div><Label>Fim</Label><Input type="time" value={avail.end_time} onChange={(e) => setAvail({ ...avail, end_time: e.target.value })} className="mt-1" data-testid="avail-end" /></div>
                <div>
                  <Label>Intervalo entre horários</Label>
                  <select value={avail.slot_minutes || 30} onChange={(e) => setAvail({ ...avail, slot_minutes: parseInt(e.target.value) })} data-testid="avail-slot-minutes" className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option value={15}>A cada 15 minutos</option>
                    <option value={30}>A cada 30 minutos (14:00, 14:30, 15:00...)</option>
                    <option value={60}>A cada 60 minutos (14:00, 15:00...)</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Dias bloqueados / Feriados</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="date" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} data-testid="holiday-input" />
                  <Button type="button" variant="outline" onClick={() => { if (newHoliday) { setAvail({ ...avail, holidays: [...(avail.holidays || []), newHoliday] }); setNewHoliday(""); } }}>Adicionar</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(avail.holidays || []).map((h) => (
                    <span key={h} className="text-xs bg-secondary rounded-full px-2 py-1 flex items-center gap-1">{h}
                      <button onClick={() => setAvail({ ...avail, holidays: avail.holidays.filter((x) => x !== h) })}><X className="w-3 h-3" /></button></span>
                  ))}
                </div>
              </div>
              <div>
                <Label>Férias (período)</Label>
                <div className="flex gap-2 mt-1 items-end flex-wrap">
                  <Input type="date" value={vac.start} onChange={(e) => setVac({ ...vac, start: e.target.value })} className="w-40" />
                  <Input type="date" value={vac.end} onChange={(e) => setVac({ ...vac, end: e.target.value })} className="w-40" />
                  <Button type="button" variant="outline" onClick={() => { if (vac.start && vac.end) { setAvail({ ...avail, vacations: [...(avail.vacations || []), vac] }); setVac({ start: "", end: "" }); } }}>Adicionar</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(avail.vacations || []).map((v, i) => (
                    <span key={i} className="text-xs bg-secondary rounded-full px-2 py-1 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{v.start} → {v.end}
                      <button onClick={() => setAvail({ ...avail, vacations: avail.vacations.filter((_, j) => j !== i) })}><X className="w-3 h-3" /></button></span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveAvail} data-testid="save-avail-btn">Salvar disponibilidade</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* meeting link dialog */}
      <Dialog open={!!linkAppt} onOpenChange={(o) => !o && setLinkAppt(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link da consulta online</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Um link exclusivo (Jitsi) foi gerado automaticamente. Você pode substituí-lo pelo seu próprio link (Zoom, Google Meet, etc).</p>
            <div><Label>Link da reunião</Label><Input value={linkVal} onChange={(e) => setLinkVal(e.target.value)} data-testid="meeting-link-input" className="mt-1" placeholder="https://..." /></div>
          </div>
          <DialogFooter><Button onClick={saveLink} data-testid="save-meeting-link-btn"><Link2 className="w-4 h-4 mr-1" />Salvar link</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
